import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, MapPin, CalendarDays, ArrowLeft, Tag, Clock } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const empty = {
  full_name: "",
  email: "",
  phone: "",
  company: "",
  role: "",
  dietary: "",
  bring_guest: false,
  guest_name: "",
  message: "",
  billing_nif: "",
  billing_address: "",
  billing_company_nif: "",
};

const pickLocalized = (ev, base, lang) => {
  if (!ev) return "";
  return ev[`${base}_${lang}`] || ev[`${base}_pt`] || ev[`${base}_en`] || ev[`${base}_es`] || "";
};

export default function EventDetail() {
  const { slug } = useParams();
  const { t, lang } = useLang();
  const [event, setEvent] = useState(null);
  const [status, setStatus] = useState("loading"); // loading | ok | not_found
  const [form, setForm] = useState(empty);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/events/${slug}`);
        if (!cancelled) {
          setEvent(r.data);
          setStatus("ok");
        }
      } catch (e) {
        if (!cancelled) setStatus("not_found");
      }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await axios.post(`${API}/events/${slug}/rsvp`, { ...form, language: lang });
      const rsvp = r.data;
      if (event?.price_eur > 0) {
        try {
          const checkout = await axios.post(`${API}/payments/checkout/${rsvp.id}`, {
            origin_url: window.location.origin,
          });
          if (checkout.data?.url) {
            window.location.href = checkout.data.url;
            return;
          }
        } catch (paymentErr) {
          // continue and show success — admin can reconcile manually
        }
      }
      setSubmitted(true);
      const anchor = document.getElementById("rsvp-anchor");
      if (anchor) window.scrollTo({ top: anchor.offsetTop - 80, behavior: "smooth" });
    } catch (err) {
      toast.error("Erro ao registar inscrição.");
    } finally {
      setLoading(false);
    }
  };

  if (status === "loading") {
    return (
      <div className="container-page pt-32 pb-32" data-testid="event-loading">
        <p className="text-[hsl(var(--muted-foreground))]">A carregar evento…</p>
      </div>
    );
  }

  if (status === "not_found" || !event) {
    return (
      <div className="container-page pt-32 pb-32" data-testid="event-not-found">
        <div className="overline">Evento</div>
        <h1 className="display-heading text-4xl sm:text-5xl mt-3">Evento não encontrado.</h1>
        <p className="mt-6 text-[hsl(var(--muted-foreground))]">
          O evento que procura não existe ou já não está ativo.
        </p>
        <Link
          to="/eventos"
          data-testid="event-back-to-list"
          className="mt-8 inline-flex items-center gap-2 text-sm uppercase tracking-[0.2em] text-[hsl(var(--primary))] font-medium"
        >
          <ArrowLeft className="h-4 w-4" /> Ver todos os eventos
        </Link>
      </div>
    );
  }

  const title = pickLocalized(event, "title", lang) || event.slug;
  const subtitle = pickLocalized(event, "subtitle", lang);
  const body = pickLocalized(event, "body", lang);
  const paragraphs = body ? body.split(/\n{2,}/).map((p) => p.trim()).filter(Boolean) : [];

  return (
    <div data-testid="event-detail-page">
      <section className="relative overflow-hidden bg-[hsl(var(--primary))]">
        {event.image_url && (
          <div className="absolute inset-0 opacity-25">
            <img src={event.image_url} alt="" className="w-full h-full object-cover" />
          </div>
        )}
        <div className="relative container-page pt-24 pb-24 sm:pt-32 sm:pb-32 text-white">
          <Link
            to="/eventos"
            className="inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase text-white/75 hover:text-white"
            data-testid="event-back-link"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Eventos
          </Link>
          <h1 className="display-heading text-5xl sm:text-6xl lg:text-7xl mt-5 leading-[1.05] max-w-4xl" data-testid="event-title">
            {title}
          </h1>
          {subtitle && (
            <p className="mt-8 text-lg text-white/85 max-w-2xl leading-relaxed" data-testid="event-subtitle">
              {subtitle}
            </p>
          )}
          <div className="mt-10 flex flex-wrap gap-x-8 gap-y-3 text-sm text-white/90">
            {event.date_str && (
              <span className="inline-flex items-center gap-2" data-testid="event-date">
                <CalendarDays className="h-4 w-4 text-[hsl(var(--accent))]" /> {event.date_str}
              </span>
            )}
            {(event.start_time || event.end_time) && (
              <span className="inline-flex items-center gap-2" data-testid="event-time">
                <Clock className="h-4 w-4 text-[hsl(var(--accent))]" />
                {[event.start_time, event.end_time].filter(Boolean).join(" – ")}
                {event.duration_minutes ? ` · ${formatDurationLabel(event.duration_minutes)}` : ""}
              </span>
            )}
            {event.location && (
              <span className="inline-flex items-center gap-2" data-testid="event-location">
                <MapPin className="h-4 w-4 text-[hsl(var(--accent))]" /> {event.location}
              </span>
            )}
            {event.price_eur > 0 && (
              <span className="inline-flex items-center gap-2" data-testid="event-price-tag">
                <Tag className="h-4 w-4 text-[hsl(var(--accent))]" /> {Number(event.price_eur).toFixed(0)} €
              </span>
            )}
          </div>
        </div>
      </section>

      {paragraphs.length > 0 && (
        <section className="container-narrow py-20" data-testid="event-body">
          <div className="prose-narrow space-y-6 text-[hsl(var(--foreground))] leading-relaxed">
            {paragraphs.map((p, i) => (
              <p key={i} className="text-base sm:text-lg">{p}</p>
            ))}
          </div>
        </section>
      )}

      <section className="bg-[hsl(var(--secondary))] py-24" id="rsvp-anchor">
        <div className="container-narrow">
          <div className="overline">Inscrição</div>
          <h2 className="display-heading text-4xl sm:text-5xl mt-4">Inscreva-se neste evento.</h2>
          <span className="accent-rule mt-6 block" />
          <p className="mt-6 text-[hsl(var(--muted-foreground))] leading-relaxed">
            {event.price_eur > 0
              ? `A inscrição tem um valor de ${Number(event.price_eur).toFixed(2)} €. Após o envio do formulário será redirecionado para pagamento seguro.`
              : "A inscrição é gratuita. Confirme a sua presença abaixo."}
          </p>

          {submitted ? (
            <div className="mt-10 bg-white border border-[hsl(var(--primary))] p-10" data-testid="event-rsvp-success">
              <CheckCircle2 className="h-10 w-10 text-[hsl(var(--primary))]" />
              <h3 className="display-heading text-3xl mt-5 text-[hsl(var(--primary))]">Inscrição registada.</h3>
              <p className="mt-4 text-[hsl(var(--muted-foreground))] leading-relaxed">
                Obrigado. Recebemos a sua inscrição. Receberá um email de confirmação com os detalhes finais em breve.
              </p>
            </div>
          ) : (
            <form className="mt-10 bg-white border border-[hsl(var(--border))] p-8 sm:p-10 space-y-6" onSubmit={submit} data-testid="event-rsvp-form">
              <div className="grid sm:grid-cols-2 gap-6">
                <Field label="Nome completo" required>
                  <input required value={form.full_name} onChange={(e) => set("full_name", e.target.value)} className="form-input" data-testid="event-rsvp-fullname" />
                </Field>
                <Field label="Email" required>
                  <input type="email" required value={form.email} onChange={(e) => set("email", e.target.value)} className="form-input" data-testid="event-rsvp-email" />
                </Field>
                <Field label="Telefone (opcional)">
                  <input value={form.phone} onChange={(e) => set("phone", e.target.value)} className="form-input" data-testid="event-rsvp-phone" />
                </Field>
                <Field label="Empresa (opcional)">
                  <input value={form.company} onChange={(e) => set("company", e.target.value)} className="form-input" data-testid="event-rsvp-company" />
                </Field>
                <Field label="Cargo (opcional)">
                  <input value={form.role} onChange={(e) => set("role", e.target.value)} className="form-input" data-testid="event-rsvp-role" />
                </Field>
                {event.has_meal && (
                  <Field label="Restrições alimentares (opcional)">
                    <input value={form.dietary} onChange={(e) => set("dietary", e.target.value)} className="form-input" data-testid="event-rsvp-dietary" />
                  </Field>
                )}
              </div>

              <div className="border-t border-[hsl(var(--border))] pt-6">
                <label className="flex items-start gap-3 cursor-pointer" data-testid="event-rsvp-guest-toggle">
                  <input type="checkbox" checked={form.bring_guest} onChange={(e) => set("bring_guest", e.target.checked)} className="mt-1 accent-[hsl(var(--primary))]" />
                  <span className="text-sm">Pretendo levar um acompanhante.</span>
                </label>
                {form.bring_guest && (
                  <div className="mt-4">
                    <Field label="Nome do acompanhante">
                      <input value={form.guest_name} onChange={(e) => set("guest_name", e.target.value)} className="form-input" data-testid="event-rsvp-guest-name" />
                    </Field>
                  </div>
                )}
              </div>

              <Field label="Mensagem (opcional)">
                <textarea rows={4} value={form.message} onChange={(e) => set("message", e.target.value)} className="form-input resize-none" data-testid="event-rsvp-message" />
              </Field>

              {event.price_eur > 0 && (
                <details className="border-t border-[hsl(var(--border))] pt-6 group" data-testid="event-rsvp-billing-section">
                  <summary className="cursor-pointer list-none flex items-center justify-between text-sm text-[hsl(var(--primary))] font-medium">
                    <span>Dados para Declaração de Donativo <span className="text-[hsl(var(--muted-foreground))]">(opcional)</span></span>
                    <span className="text-xs transition-transform group-open:rotate-180">▼</span>
                  </summary>
                  <p className="mt-3 text-xs text-[hsl(var(--muted-foreground))] leading-relaxed">
                    A Associação CIEES Portugal não emite faturas. Se precisar de uma Declaração de Donativo para efeitos contabilísticos, preencha estes dados.
                  </p>
                  <div className="mt-5 grid sm:grid-cols-2 gap-6">
                    <Field label="NIF">
                      <input value={form.billing_nif} onChange={(e) => set("billing_nif", e.target.value)} className="form-input" data-testid="event-rsvp-billing-nif" />
                    </Field>
                    <Field label="NIF da empresa (se aplicável)">
                      <input value={form.billing_company_nif} onChange={(e) => set("billing_company_nif", e.target.value)} className="form-input" data-testid="event-rsvp-billing-company-nif" />
                    </Field>
                  </div>
                  <div className="mt-4">
                    <Field label="Morada fiscal">
                      <input value={form.billing_address} onChange={(e) => set("billing_address", e.target.value)} className="form-input" data-testid="event-rsvp-billing-address" />
                    </Field>
                  </div>
                </details>
              )}

              <button type="submit" disabled={loading} className="btn-primary disabled:opacity-60" data-testid="event-rsvp-submit">
                {loading ? "A enviar..." : (event.price_eur > 0 ? `Inscrever e pagar (${Number(event.price_eur).toFixed(0)} €)` : "Confirmar inscrição")}
              </button>
            </form>
          )}
        </div>
      </section>

      <style>{`
        .form-input { width: 100%; background: white; border: 1px solid hsl(var(--border)); padding: 10px 12px; font-family: "Outfit", sans-serif; font-size: 15px; color: hsl(var(--foreground)); }
        .form-input:focus { outline: none; border-color: hsl(var(--accent)); }
      `}</style>
    </div>
  );
}

function Field({ label, children, required }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-[0.18em] text-[hsl(var(--muted-foreground))] mb-2">
        {label} {required && <span className="text-[hsl(var(--accent))]">*</span>}
      </label>
      {children}
    </div>
  );
}

function formatDurationLabel(minutes) {
  if (!minutes || minutes <= 0) return "";
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h && m) return `${h}h ${m}min`;
  if (h) return `${h}h`;
  return `${m}min`;
}
